/* eslint-disable no-unused-vars */ // Contains legacy/future-use code kept for potential features
import React, { useEffect, useMemo, useRef, useState } from 'react';
import seed from '../data/controlroom_seed.json';
import { API_BASE_URL } from '../config';

// Stable empty array to avoid triggering useMemo recomputation on every render
// (Using [] inline creates a new reference each render, causing shuffle loops)
const EMPTY_POSTS = [];

const RECENT_SCENES_STORAGE_KEY = 'recentScenes_v2';
const LEGACY_RECENT_SCENES_KEY = 'recentScenes';
const DEFAULT_HIGHLIGHTS_HOURS = 72;

function loadRecentScenesFromStorage() {
  try {
    localStorage.removeItem(LEGACY_RECENT_SCENES_KEY);
    const raw = localStorage.getItem(RECENT_SCENES_STORAGE_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw);
    const fetchedAt = Number(payload?.fetchedAt);
    const hoursWindow = Number(payload?.hoursWindow) || DEFAULT_HIGHLIGHTS_HOURS;
    const maxAgeMs = hoursWindow * 60 * 60 * 1000;
    if (!fetchedAt || Number.isNaN(fetchedAt) || Date.now() - fetchedAt > maxAgeMs) {
      localStorage.removeItem(RECENT_SCENES_STORAGE_KEY);
      return null;
    }
    const items = payload?.highlights;
    if (!Array.isArray(items) || !items.length) return null;
    return { highlights: items, hoursWindow };
  } catch {
    try { localStorage.removeItem(RECENT_SCENES_STORAGE_KEY); } catch {}
    return null;
  }
}

function saveRecentScenesToStorage(highlights, hoursWindow) {
  try {
    localStorage.setItem(RECENT_SCENES_STORAGE_KEY, JSON.stringify({
      fetchedAt: Date.now(),
      hoursWindow: hoursWindow || DEFAULT_HIGHLIGHTS_HOURS,
      highlights,
    }));
  } catch {}
}

// Resilient fetch with brief retry for transient 404/502/504
async function fetchWithRetry(url, init = {}, retries = 2) {
  let last;
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url, init);
      if (!res.ok && (res.status === 404 || res.status === 502 || res.status === 504)) {
        await new Promise(r => setTimeout(r, 250));
        last = new Error(`HTTP ${res.status}`);
        continue;
      }
      return res;
    } catch (e) {
      last = e;
      await new Promise(r => setTimeout(r, 250));
    }
  }
  throw last || new Error('fetch failed');
}


export default function ControlRoom() {
  const [state, setState] = useState(null);
  const [highlights, setHighlights] = useState(() => (Array.isArray(seed?.highlights) ? seed.highlights : []));
  const [initialFast, setInitialFast] = useState(true);
  const [avatars, setAvatars] = useState({});
  const [scoreboard, setScoreboard] = useState({ top_scenes: (seed?.scoreboard?.top_scenes)||[], top_posts: (seed?.scoreboard?.top_posts)||[], top_standalone: (seed?.scoreboard?.top_standalone)||[], top_characters: (seed?.scoreboard?.top_characters)||[], all_time_scene: null, all_time_post: null, all_time_character: null, window_used: 24 });
  const [, setErr] = useState(''); // Used in catch block, value not read
  // Persist the active cue for up to 10 minutes after it first appears
  const [persistedCue, setPersistedCue] = useState(null);
  const [persistedSince, setPersistedSince] = useState(0);
  const [feedWindowHours, setFeedWindowHours] = useState(DEFAULT_HIGHLIGHTS_HOURS);
  const [briefPicks, setBriefPicks] = useState([]);
  const insightsCacheRef = useRef({});

  useEffect(() => {
    let alive = true;
    // Hydrate highlights from TTL-bounded local cache for instant first paint
    const cachedPayload = loadRecentScenesFromStorage();
    if (cachedPayload && alive) {
      setHighlights(cachedPayload.highlights);
      setFeedWindowHours(cachedPayload.hoursWindow);
    }
    const fetchState = async () => {
      try {
        const r = await fetchWithRetry(`${API_BASE_URL}/api/state`);
        const dj = await r.json();
        if (alive) {
          setState(dj);
          const cs = dj && dj.current_scene;
          if (cs && cs.cue_id) {
            // If a new cue appears, start (or refresh) the 10-minute persistence window
            if (!persistedCue || persistedCue.cue_id !== cs.cue_id) {
              setPersistedCue(cs);
              setPersistedSince(Date.now());
            }
          }
        }
      } catch (e) {
        if (alive) setErr('Live unavailable');
      }
    };
    const fetchHighlights = async () => {
      try {
        let windowHours = DEFAULT_HIGHLIGHTS_HOURS;
        const r = await fetchWithRetry(`${API_BASE_URL}/api/highlights/recent?limit=8&hours=${windowHours}`);
        const dj = await r.json();
        let arr = (dj && (dj.highlights || dj.rows)) || [];
        if (dj && dj.hours_window) windowHours = Number(dj.hours_window) || windowHours;
        // Ensure at least 6 by expanding window if needed
        if (!Array.isArray(arr) || arr.length < 6) {
          try {
            windowHours = 168;
            const r2 = await fetchWithRetry(`${API_BASE_URL}/api/highlights/recent?limit=10&hours=${windowHours}`);
            const dj2 = await r2.json();
            const arr2 = (dj2 && (dj2.highlights || dj2.rows)) || [];
            if (dj2 && dj2.hours_window) windowHours = Number(dj2.hours_window) || windowHours;
            const seen = new Set((arr||[]).map(x => x.scene_id));
            for (const it of (arr2||[])) { if (!seen.has(it.scene_id)) { arr.push(it); seen.add(it.scene_id); if (arr.length>=6) break; } }
          } catch {}
        }
        // Shuffle once to avoid repeating the same first scene
        if (Array.isArray(arr)) {
          const copy = arr.slice();
          for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
          }
          if (alive) {
            setHighlights(copy);
            setFeedWindowHours(windowHours);
            saveRecentScenesToStorage(copy, windowHours);
            // Prefetch nearest quotes for first lines of each scene
            try {
              const texts = [];
              for (const sc of copy) {
                const posts = (sc.posts || []).slice(0, 3);
                for (const p of posts) {
                  const t = String(p.content || '').trim();
                  if (t) texts.push(t.slice(0, 200));
                }
              }
              if (texts.length) {
                const rr = await fetch(`${API_BASE_URL}/api/feed/insights/batch`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ texts })});
                if (rr.ok) {
                  const dj3 = await rr.json();
                  const items = Array.isArray(dj3?.items) ? dj3.items : [];
                  const cache = insightsCacheRef.current;
                  for (let i=0;i<texts.length && i<items.length;i++) {
                    cache[texts[i]] = items[i];
                  }
                }
              }
            } catch {}
          }
        } else {
          if (alive) setHighlights([]);
        }
      } catch {}
    };
    const fetchAvatars = async () => { try { const r = await fetchWithRetry(`${API_BASE_URL}/api/ui/chat/avatars`); const dj = await r.json(); if (alive) setAvatars(dj || {}); } catch (e) { console.error('avatars load failed', e); } };
    const fetchScoreboard = async () => { try { const r = await fetchWithRetry(`${API_BASE_URL}/api/insights/scoreboard?window_hours=72&limit=6`); const dj = await r.json(); if (alive) setScoreboard(dj || {}); } catch (e) { console.error('scoreboard load failed', e); } };
    const fetchBriefTeaser = async () => {
      try {
        const r = await fetchWithRetry(`${API_BASE_URL}/api/insights/troupe-brief?gloss=0`);
        const dj = await r.json();
        const cards = Array.isArray(dj && dj.cards) ? dj.cards : [];
        const featured = cards.filter((c) => c && c.kind === 'featured' && c.excerpt && c.url);
        const picks = (featured.length ? featured : cards.filter((c) => c && c.excerpt && c.url)).slice(0, 2);
        if (alive) setBriefPicks(picks);
      } catch {}
    };
    // Stagger initial fetches to avoid overwhelming backend (200ms apart)
    fetchState();
    setTimeout(fetchHighlights, 200);
    setTimeout(fetchAvatars, 400);
    setTimeout(fetchScoreboard, 600);
    setTimeout(fetchBriefTeaser, 800);
    const id1 = setInterval(fetchState, 30000);
    const id2 = setInterval(fetchHighlights, 300000);
    const id4 = setInterval(fetchScoreboard, 300000);
    return () => { alive = false; [id1, id2, id4].forEach(clearInterval); };
  }, [persistedCue]); // eslint-disable-line react-hooks/exhaustive-deps

  const nowMs = Date.now();
  const rawActive = state?.current_scene;
  const effectiveActive = rawActive || ((persistedCue && (nowMs - persistedSince) < 10 * 60 * 1000) ? persistedCue : null);
  const cueText = effectiveActive?.cue_text || '';

  return (
    <div className="console-container">
      <div className="console-left">
        <div className="console-header">Troupe Scoreboard</div>
        <div className="console-panel twinkle">
          <ScoreboardTicker data={scoreboard} windowHours={scoreboard?.window_used || 72} />
        </div>
      </div>
      <div className="console-right">
        <div className="tab-header-block">
          <div className="console-header">Highlights Feed (last {feedWindowHours} hours)</div>
          <div className="console-subheader">
            Live troupe posts on Bluesky — follow{' '}
            <a href="https://bsky.app/profile/bitbardofficial.bsky.social" target="_blank" rel="noreferrer" style={{color:'#60a5fa'}}>@bitbardofficial</a>
            . Bluesky is the stage; Explore digs deeper.
          </div>
        </div>
        <div className="console-panel rotator">
          <LiveFeedRotator posts={EMPTY_POSTS} avatars={avatars} activeCueId={null} fallbackScenes={highlights} cueText={cueText} initialFast={false} onFirstPaint={null} />
        </div>
        <div className="troupe-picks">
          <a className="troupe-picks-ask" href="#horatio">
            {briefPicks.length > 0 ? 'Ask Horatio about these' : 'Ask Horatio'}
          </a>
          <div className="troupe-picks-why">
            {briefPicks.length > 0
              ? 'The two posts people liked most this week.'
              : 'He can tell you what the troupe has been saying.'}
          </div>
          {briefPicks.map((c) => (
            <a
              key={c.url}
              className="troupe-pick"
              href={c.url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="troupe-pick-who">{c.character}</span>
              <span className="troupe-pick-line">{openingSentence(c.excerpt)}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function openingSentence(excerpt) {
  const text = String(excerpt || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  const sentence = text.match(/^(.{24,200}?[.!?])(?:\s|$)/);
  if (sentence) return sentence[1];
  if (text.length <= 200) return text.replace(/…$/, '');
  const cut = text.slice(0, 180).replace(/\s+\S*$/, '');
  return `${cut}…`;
}

function displayCharacterName(name) {
  const raw = String(name || '').replace(/[🎭🍷🗡🎪🎨]/g, '').replace(/_/g, ' ').trim();
  const k = raw.toLowerCase();
  if (k.startsWith('lady macbeth')) return 'Lady Macbeth';
  if (k.startsWith('sir andrew')) return 'Sir Andrew';
  if (k.startsWith('falstaff')) return 'Falstaff';
  if (k.startsWith('miranda')) return 'Miranda';
  if (k.startsWith('iago')) return 'Iago';
  if (k.startsWith('bitbard')) return 'BitBard';
  if (k.startsWith('horatio')) return 'Horatio';
  return raw.replace(/\b\w/g, ch => ch.toUpperCase()) || 'Unknown';
}

function ScoreboardTicker({ data, windowHours = 72 }) {
  const chars = data?.top_characters || [];
  const bestPost = data?.all_time_post;
  const bestChar = data?.all_time_character;
  const windowLabel = `last ${windowHours}h`;
  const [avatars, setAvatars] = useState({});
  
  // Load avatars for character display
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/ui/chat/avatars`);
        if (r.ok) setAvatars(await r.json());
      } catch {}
    })();
  }, []);
  
  // Union scene-linked + standalone reply hits; rank by engagement.
  // Preferring any non-empty top_posts hid the actual reply surface.
  const postsOrdered = useMemo(() => {
    const hasContent = (p) => String(p?.content || '').trim().length > 0;
    const rx = (p) => Number(p.reactions || 0) || (Number(p.likes || 0) + Number(p.reposts || 0));
    const seen = new Set();
    const merged = [];
    for (const p of [
      ...(Array.isArray(data?.top_posts) ? data.top_posts : []),
      ...(Array.isArray(data?.top_standalone) ? data.top_standalone : []),
    ]) {
      if (!hasContent(p)) continue;
      const key = String(p.url || p.post_id || p.id || p.content || '').slice(0, 200);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(p);
    }
    return merged.sort((a, b) => rx(b) - rx(a));
  }, [data?.top_posts, data?.top_standalone]);

  const ranksOrdered = useMemo(() => {
    const roster = [
      { key: 'falstaff', label: 'Falstaff' },
      { key: 'iago', label: 'Iago' },
      { key: 'lady macbeth', label: 'Lady Macbeth' },
      { key: 'miranda', label: 'Miranda' },
      { key: 'sir andrew', label: 'Sir Andrew' },
    ];
    const byKey = {};
    for (const c of (chars || [])) {
      const raw = String(c.character || '').toLowerCase().replace(/_/g, ' ').trim();
      let k = raw;
      if (raw.startsWith('sir andrew')) k = 'sir andrew';
      else if (raw.startsWith('lady macbeth')) k = 'lady macbeth';
      else if (raw.startsWith('bitbard')) continue;
      byKey[k] = (byKey[k] || 0) + Number(c.score || 0);
    }
    return roster
      .map(r => ({ character: r.label, score: byKey[r.key] || 0 }))
      .sort((a, b) => b.score - a.score);
  }, [chars]);

  return (
    <div style={{ marginTop: 12, maxHeight: '65vh', overflowY: 'auto' }} className="console-panel">
      <div className="line" style={{ color: '#9bdcff', fontWeight: 700 }}>Scoreboard</div>
      {(bestPost || bestChar) && (
        <div className="line" style={{ color: '#7aa4c9', fontSize: 12, marginBottom: 4 }}>All-time (ever)</div>
      )}
      <div className="line" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {bestPost && (
          <div className="tile" style={{ 
            minWidth: 280,
            background: 'linear-gradient(135deg, #1a2a52 0%, #0f1a38 100%)',
            border: '2px solid #2a4a7a',
            padding: 14,
            borderRadius: 8
          }}>
            <div style={{ color: '#ffb2e4', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              🏆 Top Post (all-time)
            </div>
            {bestPost.content ? (
              <div style={{ fontSize: 13, lineHeight: 1.35, marginBottom: 6, opacity: 0.95 }}>
                “{(bestPost.content || '').slice(0, 140)}{(bestPost.content || '').length > 140 ? '…' : ''}”
              </div>
            ) : null}
            <div><a href={bestPost.url || '#'} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>View post →</a></div>
            <div style={{ fontSize: 13, marginTop: 6, color: '#9bdcff' }}>
              {displayCharacterName(bestPost.author)} · {bestPost.reactions || '—'}
            </div>
          </div>
        )}
        {bestChar && (
          <div className="tile" style={{ minWidth: 220 }}>
            <div style={{ color: '#7aa4c9' }}>Top Character (all-time)</div>
            <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <CharacterAvatar name={bestChar.character} avatars={avatars} size="md" />
              <a href={bitbardProfileUrl(bestChar.character)} target="_blank" rel="noreferrer">{displayCharacterName(bestChar.character)}</a>
            </div>
            <div style={{ fontSize: 12 }}>Score {bestChar.score}</div>
          </div>
        )}
      </div>
      {postsOrdered.length > 0 && (
      <>
      <div className="line" style={{ color: '#9bdcff', fontWeight: 700, marginTop: 6 }}>Top Posts ({windowLabel})</div>
      <div className="line" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {postsOrdered.map((p, i) => {
          const handlePostClick = () => {
            if (window.gtag) {
              window.gtag('event', 'scene_opened', {
                post_id: p.post_id || p.id || i,
                source: 'top_posts'
              });
            }
          };
          const rx = p.reactions || ((p.likes || 0) + (p.reposts || 0));
          return (
            <div key={`p-${i}`} className="tile" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <CharacterAvatar name={p.author} avatars={avatars} size="md" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={(p.url || p.scene_url || '#')} target="_blank" rel="noreferrer" style={{ lineHeight: 1.4 }} onClick={handlePostClick}>
                    "{(p.content || '').slice(0, 80)}{(p.content || '').length > 80 ? '...' : ''}"
                  </a>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    {displayCharacterName(p.author)} · {p.likes ? `♥${p.likes}` : ''} {p.reposts ? `↺${p.reposts}` : ''} {!p.likes && !p.reposts ? (rx ? `${rx}` : '— Unscored') : ''}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}
      <div className="line" style={{ color: '#9bdcff', fontWeight: 700, marginTop: 6 }}>Character Ranks ({windowLabel})</div>
      <div className="line" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ranksOrdered.map((c, i) => (
          <div key={`c-${i}`} className="tile" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CharacterAvatar name={c.character} avatars={avatars} size="md" />
            <div style={{ flex: 1 }}>
              <a href={bitbardProfileUrl(c.character)} target="_blank" rel="noreferrer">{c.character}</a>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Score {c.score}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Reusable character avatar component
function CharacterAvatar({ name, avatars, size = 'sm' }) {
  const cleanName = String(name || '').replace(/[🎭🍷🗡🎪🎨]/g, '').trim();
  const keyNorm = cleanName.toLowerCase().replace(/_/g, ' ').trim();
  const map = avatars || {};
  let avatarUrl = null;
  if (keyNorm.startsWith('falstaff')) avatarUrl = map['falstaff'] || map['Falstaff'];
  else if (keyNorm.startsWith('lady macbeth')) {
    avatarUrl = map['lady macbeth'] || map['lady_macbeth'] || map['Lady Macbeth'];
  }
  else if (keyNorm.startsWith('sir andrew')) {
    avatarUrl = map['sir andrew'] || map['sir andrew aguecheek'] || map['sir_andrew'] || map['Sir Andrew'] || map['Sir Andrew Aguecheek'];
  }
  else if (keyNorm.startsWith('miranda')) avatarUrl = map['miranda'] || map['Miranda'];
  else if (keyNorm.startsWith('iago')) avatarUrl = map['iago'] || map['Iago'];
  else if (keyNorm.startsWith('horatio')) avatarUrl = map['horatio'] || map['Horatio'];
  else if (keyNorm.startsWith('bitbard')) avatarUrl = map['bitbard'] || map['BitBard'];
  // Last resort: any key that normalizes to the same label
  if (!avatarUrl) {
    const hit = Object.keys(map).find(k => String(k).toLowerCase().replace(/_/g, ' ').trim().startsWith(keyNorm.split(' ')[0] || ''));
    if (hit) avatarUrl = map[hit];
  }
  
  const dimensions = size === 'sm' ? 20 : 28;
  
  if (avatarUrl) {
    return (
      <img 
        src={avatarUrl} 
        alt={name}
        title={name}
        style={{ 
          width: dimensions, 
          height: dimensions, 
          borderRadius: '50%',
          marginRight: 4,
          display: 'inline-block',
          verticalAlign: 'middle',
          objectFit: 'cover'
        }}
      />
    );
  }
  
  // Fallback: initials
  const parts = cleanName.split(/\s+/);
  const initials = (parts[0]?.[0] || '?') + (parts[1]?.[0] || '');
  let h = 0; 
  for (let i=0;i<cleanName.length;i++) h = (h*31 + cleanName.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  const bg = `hsl(${hue} 45% 22%)`;
  
  return (
    <div 
      title={name}
      style={{ 
        width: dimensions, 
        height: dimensions, 
        borderRadius: '50%',
        background: bg,
        color: '#e6eef6',
        fontSize: size === 'sm' ? 9 : 11,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 4,
        verticalAlign: 'middle',
        fontWeight: 500
      }}
    >
      {initials.toUpperCase()}
    </div>
  );
}

function bitbardProfileUrl(character) {
  try {
    const bskyBase = 'https://bsky.app/profile/';
    const mapBsky = {
      'falstaff': 'bbo-falstaff.bsky.social',
      'iago': 'bbo-iago.bsky.social',
      'lady macbeth': 'bbo-ladymacbeth.bsky.social',
      'miranda': 'bbo-miranda.bsky.social',
      'sir andrew': 'bbo-sirandrew.bsky.social',
      'bitbard': 'bitbardofficial.bsky.social',
    };
    let key = (character || '').toLowerCase().replace(/_/g, ' ').trim();
    if (key.startsWith('sir andrew')) key = 'sir andrew';
    if (key.startsWith('lady macbeth')) key = 'lady macbeth';
    if (key.startsWith('bitbard')) key = 'bitbard';
    if (mapBsky[key]) return `${bskyBase}${mapBsky[key]}`;
    return `${bskyBase}bitbardofficial.bsky.social`;
  } catch { return '#'; }
}

// (unused) RotatorCard

// Teletype-style replay with per-character typing over full scene
function TeletypeReplay({ item, avatars, onDone, topPosts, alwaysDazzle, fastFirst }) {
  const posts = useMemo(() => (item?.posts || []), [item?.posts]); // Memoize to prevent recreation on every render
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [forcedDazzle, setForcedDazzle] = useState(false);
  const wrapRef = useRef(null);
  const [sourceUrl, setSourceUrl] = useState('');
  // Load source sidecar for promote scenes to render "Responding to" link
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const sid = item?.scene_id;
        // Skip evidence lookup for generated reply IDs (they won't exist in database)
        if (!sid || sid.startsWith('reply_')) { setSourceUrl(''); return; }
        const r = await fetch(`${API_BASE_URL}/api/archive/evidence?scene_id=${encodeURIComponent(String(sid))}`);
        if (r.ok) {
          const dj = await r.json();
          const src = (dj && dj.source && (dj.source.bsky_url || dj.source.url)) || '';
          if (alive) setSourceUrl(src || '');
        } else {
          if (alive) setSourceUrl('');
        }
      } catch { if (alive) setSourceUrl(''); }
    })();
    return () => { alive = false };
  }, [item?.scene_id]);

  // Preserve original order from highlight scene
  const ordered = useMemo(() => {
    return Array.isArray(posts) ? posts : [];
  }, [posts]);

  // detect if first line is BitBard cue incipit
  const incipitIsFirst = useMemo(() => {
    if (!ordered.length) return false;
    const a = (ordered[0]?.author || '');
    const c = (ordered[0]?.content || '');
    return a.startsWith('BitBard') && /^🎭\s*Cue:\s*/.test(c);
  }, [ordered]);

  // helper: detect curtain line variants (colon, em dash, hyphen; Curtain or Curtain Call)
  const isCurtainLine = (txt) => {
    try {
      // Remove zero-width and formatting chars, collapse spaces
      const cleaned = String(txt || '')
        .replace(/[\u200B-\u200D\u2060-\u206F]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      // match: "Cue: Curtain", "Cue — Curtain", "Cue - Curtain", and "Curtain Call" variants
      if (/\bCue\b\s*[:—-]\s*Curtain(?:\s*Call)?\b/i.test(cleaned)) return true;
      // fallback: standalone word 'Curtain' near start or as sole token
      if (/^(?:Curtain(?:\s*Call)?)(?:\b|$)/i.test(cleaned)) return true;
      return false;
    } catch { return false; }
  };

  // build tech caption like "Southwark, 9:56 pm GMT, 1 day ago"
  const caption = useMemo(() => {
    const iso = item?.posted_at || (item?.posts && item.posts[0]?.created_at) || (item?.posts && item.posts[0]?.timestamp);
    if (!iso) return '';
    try {
      const dt = new Date(iso);
      const timeStr = dt.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' }).toLowerCase();
      const now = new Date();
      const days = Math.floor((now.getTime() - dt.getTime()) / (24*3600*1000));
      const rel = days <= 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
      return `Southwark, ${timeStr} GMT, ${rel}`;
    } catch {
      return '';
    }
  }, [item]);

  // Build a stable signature of the posts to avoid resetting on harmless poll updates
  const itemSig = useMemo(() => {
    try {
      return (item?.posts || []).map(p => `${p.author}|${p.content}|${p.timestamp||p.created_at||''}`).join('§');
    } catch { return String((item && item.posts && item.posts.length) || 0); }
  }, [item]);

  // reset only when the content signature changes (prevents restart on identical polls)
  useEffect(() => {
    if (!ordered.length) return;
    console.log(`[TeletypeReplay] RESET - ordered.length=${ordered.length}, incipitIsFirst=${incipitIsFirst}, sig=${itemSig.slice(0,40)}`);
    setLineIndex(incipitIsFirst ? 1 : 0);
    setCharIndex(0);
    setFinished(false);
  }, [itemSig, incipitIsFirst, ordered.length]);

  // per-character typing: ~10s per post regardless of length; sprint on first paint
  useEffect(() => {
    if (!ordered.length) return;
    if (lineIndex >= ordered.length) return;
    const text = (ordered[lineIndex]?.content || '');
    if (charIndex >= text.length) {
      const t = setTimeout(() => {
        if (lineIndex >= ordered.length - 1) {
          setFinished(true);
        } else {
          setLineIndex(lineIndex + 1);
          setCharIndex(0);
        }
      }, 300);
      return () => clearTimeout(t);
    }
    const duration = fastFirst ? 800 : 4000;  // Reduced from 9000 to 4000 for faster typing
    const step = Math.max(8, Math.floor(duration / Math.max(1, text.length)));
    const id = setTimeout(() => setCharIndex(ci => ci + 1), step);
    return () => clearTimeout(id);
  }, [ordered, lineIndex, charIndex, fastFirst]);

  // auto-scroll as text grows
  useEffect(() => {
    if (!wrapRef.current) return;
    try { wrapRef.current.scrollTop = wrapRef.current.scrollHeight; } catch {}
  }, [lineIndex, charIndex]);

  // trigger rotation after finish: if curtain, wait for CurtainSequence callback; else brief pause or forced dazzle
  useEffect(() => {
    if (!finished) return;
    console.log(`[TeletypeReplay] finished=true, forcedDazzle=${forcedDazzle}, lineIndex=${lineIndex}, ordered.length=${ordered.length}`);
    const lastLine = (ordered[ordered.length-1]?.content)||'';
    if (isCurtainLine(lastLine)) return; // CurtainSequence will signal onComplete
    if (alwaysDazzle && !forcedDazzle) { setForcedDazzle(true); return; }
    // Longer pause for replies (standalone replies) vs scenes - allow users to appreciate replies
    const isReply = item?.type === 'reply';
    const pauseDuration = isReply ? 6000 : 2000;  // 6 seconds for replies, 2 seconds for scenes
    console.log(`[TeletypeReplay] scheduling onDone in ${pauseDuration}ms`);
    const t = setTimeout(() => { console.log('[TeletypeReplay] calling onDone'); onDone && onDone(); }, pauseDuration);
    return () => clearTimeout(t);
  }, [finished, alwaysDazzle, forcedDazzle, onDone, ordered, item, lineIndex]); // Added item dependency

  // const activePink = '#ff3ab5'; // Unused - kept for potential future use
  const idleCyan = '#9bdcff';
  
  // Check if this is a standalone reply with parent context
  const isStandaloneReply = item?.type === 'reply' && item?.parent_post;
  const parentPost = item?.parent_post;
  
  return (
    <div className="teletype" ref={wrapRef}>
      {sourceUrl && (
        <div className="line" style={{ color: '#9bdcff' }}>
          Responding to: <a href={sourceUrl} target="_blank" rel="noreferrer">view target card</a>
        </div>
      )}
      
      {/* Show parent context for standalone replies */}
      {isStandaloneReply && parentPost && (
        <div style={{ 
          background: 'rgba(123, 104, 238, 0.1)', 
          border: '1px solid rgba(123, 104, 238, 0.3)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px',
          fontSize: '13px'
        }}>
          <div style={{ color: '#7b68ee', fontSize: '11px', marginBottom: '6px', fontWeight: 500 }}>
            ↩️ Replying to {parentPost.url ? (
              <a href={parentPost.url} target="_blank" rel="noreferrer" style={{ color: '#7b68ee' }}>
                @{parentPost.author || 'unknown'}
              </a>
            ) : `@${parentPost.author || 'unknown'}`}
          </div>
          <div style={{ color: '#d0e0ff', fontStyle: 'italic', lineHeight: '1.4' }}>
            "{parentPost.content}"
          </div>
        </div>
      )}
      
      {!!caption && <div className="teletype-caption">{caption}</div>}
      {incipitIsFirst && ordered[0] && (
        <div className="line" style={{ color: idleCyan }}>
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(ordered[0].author, avatars)})` }} />
          <strong style={{ color: '#e6eefc' }}>{ordered[0].author}:</strong> {ordered[0].content}
        </div>
      )}
      {ordered.slice(incipitIsFirst ? 1 : 0, Math.min(lineIndex, ordered.length)).map((p, i) => (
        <div
          key={i}
          className="line"
          style={{ color: idleCyan }}
        >
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(p.author, avatars)})` }} />
          <strong style={{ color: '#e6eefc' }}>{p.author}:</strong> {p.content}
        </div>
      ))}
      {lineIndex < ordered.length && (
        <div
          className="line"
          style={{ color: '#ff3ab5' }}
        >
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(ordered[lineIndex].author, avatars)})` }} />
          <strong style={{ color: '#ffd6ef' }}>{ordered[lineIndex].author}:</strong> {(ordered[lineIndex].content || '').slice(0, charIndex)}
          <span className="caret">▮</span>
        </div>
      )}
      {lineIndex >= ordered.length - 1 && finished && (
        isCurtainLine((ordered[ordered.length-1]?.content)||'') || forcedDazzle ? (
          <div style={{ marginTop: 10 }}>
            <div className="cylon-wrap"><div className="cylon-eye" /></div>
            {/* Hold for 10s then complete */}
            <DelayComplete ms={10000} onDone={() => { setForcedDazzle(false); onDone && onDone(); }} />
          </div>
        ) : null
      )}
      {item?.web_url && <div style={{ marginTop: 8 }}><a className="bsky-btn" href={item.web_url} target="_blank" rel="noreferrer">Open on Bluesky</a></div>}
    </div>
  );
}

// Live teletype that types only the newest incoming line, leaves prior lines steady, and autoscrolls down
function LiveTeletype({ posts, avatars }) {
  const ordered = Array.isArray(posts) ? posts.slice().reverse() : [];// oldest first not guaranteed; reverse recent desc to asc
  const [charIndex, setCharIndex] = useState(0);
  const wrapRef = useRef(null);

  // Advance typing when new posts arrive
  useEffect(() => {
    if (!Array.isArray(posts) || posts.length === 0) return;
    setCharIndex(0);
  }, [posts]);

  // Type only the newest line
  useEffect(() => {
    if (!Array.isArray(posts) || posts.length === 0) return;
    const latest = posts[posts.length - 1];
    const text = (latest?.content || '');
    if (charIndex >= text.length) return;
    const step = Math.max(12, Math.floor(6000 / Math.max(1, text.length)));
    const id = setTimeout(() => setCharIndex(c => c + 1), step);
    return () => clearTimeout(id);
  }, [posts, charIndex]);

  // Autoscroll to bottom
  useEffect(() => {
    if (!wrapRef.current) return;
    try { wrapRef.current.scrollTop = wrapRef.current.scrollHeight; } catch {}
  }, [posts, charIndex]);

  const idleCyan = '#9bdcff';
  return (
    <div className="teletype" ref={wrapRef} style={{ maxHeight: 260, overflowY: 'auto', marginTop: 6 }}>
      {(posts || []).slice(0, Math.max(0, posts.length - 1)).map((p, i) => (
        <div key={`live-${i}`} className="line" style={{ color: idleCyan }}>
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(p.author, avatars)})` }} />
          <strong style={{ color: '#e6eefc' }}>{p.author}:</strong> {p.content}
        </div>
      ))}
      {Array.isArray(posts) && posts.length > 0 && (
        <div className="line" style={{ color: '#ff3ab5' }}>
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(posts[posts.length - 1].author, avatars)})` }} />
          <strong style={{ color: '#ffd6ef' }}>{posts[posts.length - 1].author}:</strong> {(posts[posts.length - 1].content || '').slice(0, charIndex)}
          <span className="caret">▮</span>
        </div>
      )}
    </div>
  );
}

// Rotates the five most recent live posts as a mini-scene with teletype reveal.
function LiveFeedRotator({ posts, avatars, activeCueId, fallbackScenes, cueText, initialFast, onFirstPaint }) {
  // Group posts by scene_id; ensure BitBard cue first, then replies in chronological order
  const scenes = useMemo(() => {
    const arr = Array.isArray(posts) ? posts.slice() : [];
    const byScene = new Map();
    for (const p of arr) {
      const sid = p.scene_id || p.scene || p.thread_id || 'misc';
      if (!byScene.has(sid)) byScene.set(sid, []);
      byScene.get(sid).push(p);
    }
      const orderedScenes = Array.from(byScene.entries()).map(([sid, list]) => {
      // Separate incipit (first BitBard), character replies, and curtain (last BitBard)
      const bitbardPosts = list.filter(x => (x.author||'').startsWith('BitBard'))
        .sort((a,b) => (new Date(a.created_at||a.timestamp||0)) - (new Date(b.created_at||b.timestamp||0)));
      const incipit = bitbardPosts[0]; // First BitBard post is cue
      const curtain = bitbardPosts.length > 1 ? bitbardPosts[bitbardPosts.length - 1] : null; // Last BitBard post is curtain
      const replies = list.filter(x => !(x.author||'').startsWith('BitBard'))
        .sort((a,b) => (new Date(a.created_at||a.timestamp||0)) - (new Date(b.created_at||b.timestamp||0)));
      // Build complete scene: incipit → character replies → curtain
      let postsOrdered = [incipit, ...replies, curtain].filter(Boolean);
      // If incomplete or misordered (no incipit or curtain first), try to merge from highlights fallback
      const looksLikeCurtain = (txt) => /\bCurtain(\s*Call)?\b/i.test(String(txt||''));
      if (((!incipit) || looksLikeCurtain(postsOrdered[0]?.content) || postsOrdered.length < 3) && Array.isArray(fallbackScenes)) {
        const hit = fallbackScenes.find(h => String(h.scene_id) === String(sid));
        if (hit && Array.isArray(hit.posts)) {
          const seen = new Set(postsOrdered.map(pp => (pp.author||'')+ '|' + (pp.content||'')));
          const more = hit.posts
            .map(p => ({ author: p.author, content: p.content, created_at: p.timestamp, timestamp: p.timestamp }))
            .filter(p => { const key = (p.author||'')+'|'+(p.content||''); if (seen.has(key)) return false; seen.add(key); return true; })
            .sort((a,b) => (new Date(a.created_at||a.timestamp||0)) - (new Date(b.created_at||b.timestamp||0)));
          // Ensure incipit first
          const inc2 = more.find(x => (x.author||'').startsWith('BitBard'));
          const rest2 = more.filter(x => !(x.author||'').startsWith('BitBard'));
          postsOrdered = [incipit || inc2, ...replies, ...rest2].filter(Boolean);
          // If still short, keep appending remaining highlight posts
          if (postsOrdered.length < 3) {
            postsOrdered = [incipit || inc2, ...more.filter(Boolean)];
          }
        }
      }
      // Guard: require incipit first and avoid curtain-only stubs
      if (!postsOrdered.length || !(postsOrdered[0]?.author||'').startsWith('BitBard')) return null;
      if (looksLikeCurtain(postsOrdered[0]?.content)) return null;
      const postedAt = (incipit && (incipit.created_at || incipit.timestamp)) || (list[list.length-1]?.timestamp) || null;
      return { posts: postsOrdered, posted_at: postedAt, scene_id: sid, type: 'scene' };
    });
    let cleaned = orderedScenes.filter(Boolean);
    cleaned.sort((a,b) => (new Date(b.posted_at||0)) - (new Date(a.posted_at||0)));
    // Filter out incomplete scenes (must have at least 3 posts: incipit + 1 response + curtain)
    cleaned = cleaned.filter(sc => sc.posts && sc.posts.length >= 3);
    cleaned = cleaned.slice(0, 5);
    
    // Top-up from highlights: include both full scenes AND standalone replies
    if (Array.isArray(fallbackScenes) && fallbackScenes.length) {
      const seenSig = new Set(cleaned.map(s => (s.posts && s.posts[0] ? ((s.posts[0].author||'') + '|' + (s.posts[0].content||'')) : '')));
      for (const h of fallbackScenes) {
        if (cleaned.length >= 8) break; // Allow up to 8 items (scenes + replies)
        
        // Handle standalone replies (type: 'reply')
        if (h.type === 'reply' && Array.isArray(h.posts) && h.posts.length > 0) {
          const replyPost = h.posts[0];
          const sig = (replyPost.author||'') + '|' + (replyPost.content||'');
          if (!seenSig.has(sig)) {
            // Use content-based stable ID instead of Math.random() to prevent re-renders
            const stableId = h.scene_id || 'reply_' + (replyPost.content || '').slice(0, 20).replace(/\W/g, '_');
            cleaned.push({ 
              posts: h.posts, 
              posted_at: h.posted_at || replyPost.timestamp, 
              scene_id: stableId,
              type: 'reply',
              parent_post: h.parent_post // Include parent context for display
            });
            seenSig.add(sig);
          }
          continue;
        }
        
        // Handle full scenes (type: 'scene' or no type)
        const posts = Array.isArray(h.posts) ? h.posts.map(p => ({ author: p.author, content: p.content, created_at: p.timestamp, timestamp: p.timestamp })) : [];
        if (posts.length < 3) continue; // Skip incomplete scenes
        const sig = posts.length ? ((posts[0].author||'') + '|' + (posts[0].content||'')) : '';
        if (sig && !seenSig.has(sig)) {
          cleaned.push({ posts, posted_at: h.posted_at || (h.posts && h.posts[0] && h.posts[0].timestamp) || null, type: 'scene' });
          seenSig.add(sig);
        }
      }
    }
    
    // Note: Parent (fetchHighlights) already shuffles before setting state.
    // Don't reshuffle here or we get racing behavior when fallbackScenes updates.
    return cleaned.slice(0, 8); // Return up to 8 items for variety
  }, [posts, fallbackScenes]);

  const [index, setIndex] = useState(0);
  useEffect(() => { setIndex(0); }, [activeCueId]);
  
  // Lock in scenes to prevent mid-typing restarts when fallbackScenes updates
  const lockedScenesRef = useRef(null);
  const [lockedScenes, setLockedScenes] = useState([]);
  
  // Only update locked scenes when: (a) we have none, or (b) a full rotation completes
  useEffect(() => {
    if (!Array.isArray(scenes) || !scenes.length) return;
    if (!lockedScenesRef.current || lockedScenesRef.current.length === 0) {
      lockedScenesRef.current = scenes;
      setLockedScenes(scenes);
    }
  }, [scenes]);
  
  // If a live cue is active, prioritize the scene whose incipit matches the cue text
  const prioritizedScenes = useMemo(() => {
    const source = lockedScenes.length ? lockedScenes : scenes;
    if (!Array.isArray(source) || !source.length) return source;
    try {
      const cx = (cueText || '').toLowerCase().slice(0, 80);
      const liveIdx = source.findIndex(sc => (
        Array.isArray(sc.posts) && sc.posts.length > 0 &&
        (sc.posts[0].author || '').startsWith('BitBard') &&
        ((sc.posts[0].content || '').toLowerCase().includes(cx))
      ));
      if (liveIdx >= 0) {
        const copy = source.slice();
        const [live] = copy.splice(liveIdx, 1);
        return [live, ...copy];
      }
      return source;
    } catch { return source; }
  }, [lockedScenes, scenes, cueText]);
  
  // Add failsafe timeout to prevent infinite hang on a scene (must come AFTER prioritizedScenes definition)
  useEffect(() => {
    const maxTime = 45000; // 45 seconds max per scene
    const timer = setTimeout(() => {
      console.warn('[LiveFeedRotator] Scene display timeout, advancing to next');
      setIndex(i => (i + 1) % Math.max(1, prioritizedScenes.length));
    }, maxTime);
    return () => clearTimeout(timer);
  }, [index, prioritizedScenes.length]);

  const item = React.useMemo(() => prioritizedScenes[index] || { posts: [] }, [prioritizedScenes, index]);
  
  // DEBUG: Log scene transitions to diagnose racing
  useEffect(() => {
    const preview = item?.posts?.[0]?.content?.slice(0, 40) || 'empty';
    console.log(`[LiveFeedRotator] index=${index}, scenes=${prioritizedScenes.length}, preview="${preview}"`);
  }, [index, prioritizedScenes.length, item]);
  
  useEffect(() => { if (initialFast && prioritizedScenes.length) { try { onFirstPaint && onFirstPaint(); } catch {} } }, [initialFast, prioritizedScenes, onFirstPaint]);
  const handleDone = () => {
    console.log(`[LiveFeedRotator] handleDone called, current index=${index}`);
    setTimeout(() => {
      setIndex(i => {
        const next = (i + 1) % Math.max(1, prioritizedScenes.length || 1);
        // After a full rotation, allow scenes to refresh from latest data
        if (next === 0 && scenes.length) {
          lockedScenesRef.current = scenes;
          setLockedScenes(scenes);
        }
        return next;
      });
    }, 250);
  };
  return (
    <TeletypeReplay
      key={(item.posted_at||'') + ':' + index}
      item={item}
      avatars={avatars}
      topPosts={[]}
      onDone={handleDone}
      alwaysDazzle
      fastFirst={initialFast}
    />
  );
}

// Build a lightweight, console-style Scene DNA text block for hover popover
function buildSceneDNAText(item) {
  try {
    const posts = Array.isArray(item?.posts) ? item.posts : [];
    const incipit = posts.find(p => (p.author||'').startsWith('BitBard'));
    const firstReply = posts.find(p => !(p.author||'').startsWith('BitBard'));
    const headline = (incipit?.content || '').slice(0, 180);
    const replySnip = (firstReply?.content || '').slice(0, 120);
    const host = (item?.story_url || '').split('/')[2] || (item?.web_url || '').split('/')[2] || '';
    const embed = host ? 'External' : 'Record/Image';
    const llm = 'off';
    const bars = (v) => {
      const n = Math.max(0, Math.min(10, Math.round(v * 10)));
      return '▰'.repeat(n) + '▏'.repeat(10 - n);
    };
    // Fake node confidences heuristically from content
    const txt = `${incipit?.content || ''} ${replySnip}`.toLowerCase();
    const sc = /coach|team|game|goal|score/.test(txt) ? 0.82 : /king|duke|crown/.test(txt) ? 0.58 : 0.41;
    const hc = /honor|duty|vow/.test(txt) ? 0.66 : 0.48;
    const fc = /fate|omens|prophecy|doom/.test(txt) ? 0.44 : 0.32;
    const seed = Math.min(0.85, 0.30 + sc * 0.3 + hc * 0.2);
    const novel = Math.max(0.15, 1 - seed);
    const lines = [];
    lines.push(`🎭 Scene DNA — ${host || 'bitbard'}${item?.cue_type ? ` • ${item.cue_type}` : ''}`);
    lines.push(`🏷️ Source: highlights • 🌐 ${host || '—'} • 🧵 Embed: ${embed} • 🤖 LLM: ${llm}`);
    if (headline) lines.push(`🧪 Seeds: ${headline}`);
    if (replySnip) lines.push(`🗣️ First reply: ${replySnip}`);
    // Remove heuristic nodes line
    // lines.push(`🧬 Nodes: sports(${sc.toFixed(2)}) ${bars(sc)}  honor(${hc.toFixed(2)}) ${bars(hc)}  fate(${fc.toFixed(2)}) ${bars(fc)}`);
    lines.push(`📈 Coverage: Seed ${(seed*100|0)}% | Novel ${(novel*100|0)}%  ${bars(seed)}`);
    if (item?.web_url) lines.push(`🔗 Root: ${item.web_url}`);
    return lines.join('\n');
  } catch { return 'Scene DNA unavailable'; }
}

// A single line with avatar + hover DNA popover derived from its text
function LineWithDNA({ author, text, displayText, avatars, color, typing, topPosts, insightsCacheRef }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dna, setDna] = useState('');
  const [insight, setInsight] = useState(null);
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      className="line"
      style={{ color: color || '#9bdcff', position: 'relative' }}
        onMouseEnter={async (e) => {
        setDna(buildLineDNAText(author, text, topPosts));
        setPos({ x: e.clientX + 12, y: e.clientY + 12 });
        setShow(true);
          const key = String(text || '').slice(0, 200);
          const pref = insightsCacheRef.current[key];
          if (pref) { setInsight(pref); return; }
        // Fetch nearest quote insight (best-effort)
        try {
          const r = await fetch(`${API_BASE_URL}/api/feed/insights`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ text })});
          if (r.ok) {
            const dj = await r.json();
            setInsight(dj || null);
              insightsCacheRef.current[key] = dj || null;
          }
        } catch {}
      }}
      onMouseMove={(e) => show && setPos({ x: e.clientX + 12, y: e.clientY + 12 })}
      onMouseLeave={() => setShow(false)}
    >
      <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(author, avatars)})` }} />
      <strong style={{ color: color ? (color === '#ff3ab5' ? '#ffd6ef' : '#e6eefc') : '#e6eefc' }}>{author}:</strong> {displayText != null ? displayText : text}
      {typing && <span className="caret">▮</span>}
      {show && (
        <div className="dna-popover" style={{ left: pos.x, top: pos.y }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{dna}{insight && insight.nearest_quote ? `\n\nClosest Shakespeare (pilot): "${insight.nearest_quote.text}" (${insight.nearest_quote.cite})` : ''}</pre>
        </div>
      )}
    </div>
  );
}

// Build DNA focused on rhetorical “ingredients” from a single line (apothecary style)
function buildLineDNAText(author, text, topPosts) {
  try {
    const t = (text || '').toLowerCase();
    const chips = [];
    const meter = (v) => {
      const n = Math.max(0, Math.min(10, Math.round(v * 10)));
      return '▰'.repeat(n) + '▏'.repeat(10 - n);
    };
    // Heuristic nodes only (suppress near-zeros)
    const nodeSports = /(coach|team|match|score|goal|ship)/.test(t) ? 0.7 : 0.0;
    const nodeHonor = /(\bhonor\b|\bvow\b|\bduty\b|\bfaith\b)/.test(t) ? 0.5 : 0.0;
    const nodeFate = /(\bfate\b|\bfortune\b|omin|prophec|\bdoom\b)/.test(t) ? 0.4 : 0.0;
    // Link to current top post if this line closely matches
    let reactionLine = '';
    try {
      const tops = Array.isArray(topPosts) ? topPosts : [];
      const best = tops
        .map(p => ({ url: p.scene_url || p.url, content: (p.content || ''), score: jaccardLike(p.content || '', text || '') }))
        .sort((a,b) => b.score - a.score)[0];
      if (best && best.score > 0.25 && best.url) {
        reactionLine = `🏆 Related top post (+${(tops.find(tp => (tp.content||'')===best.content)?.reactions)||'?'}): ${best.url}`;
      }
    } catch {}
    chips.push(`🧬 Nodes`);
    if (nodeSports > 0.15) chips.push(`• sports ${meter(nodeSports)} (${nodeSports.toFixed(2)})`);
    if (nodeHonor > 0.15) chips.push(`• honor ${meter(nodeHonor)} (${nodeHonor.toFixed(2)})`);
    if (nodeFate > 0.15) chips.push(`• fate ${meter(nodeFate)} (${nodeFate.toFixed(2)})`);
    return `${author}\n${text}\n\n${chips.join('\n')}${reactionLine ? `\n${reactionLine}` : ''}`;
  } catch { return 'Rhetorical DNA unavailable'; }
}

// Lightweight token overlap scorer
function jaccardLike(a, b) {
  try {
    const ta = (a||'').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const tb = (b||'').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!ta.length || !tb.length) return 0;
    const sa = new Set(ta);
    const sb = new Set(tb);
    let inter = 0;
    for (const t of sa) if (sb.has(t)) inter++;
    const denom = Math.max(sa.size, sb.size);
    return inter / denom;
  } catch { return 0; }
}

function CurtainSparkle() {
  // simple shooting star with CSS animation
  return (
    <div className="sparkle-wrap">
      <div className="sparkle" />
      <div className="sparkle" style={{ animationDelay: '0.2s', top: '22%' }} />
      <div className="sparkle" style={{ animationDelay: '0.4s', top: '44%' }} />
    </div>
  );
}

function CurtainSequence({ onComplete }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const totalMs = 12000; // 12s hold for clearer sizzle
    const start = performance.now();
    let raf = 0;
    const doneTimer = setTimeout(() => { try { onComplete && onComplete(); } catch {} }, totalMs);
    const tick = (t) => {
      const e = Math.min(1, (t - start) / totalMs);
      setElapsed(e);
      if (e < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); clearTimeout(doneTimer); };
  }, [onComplete]);
  const bands = new Array(30).fill(0);
  return (
    <div />
  );
}

function DelayComplete({ ms, onDone }) {
  useEffect(() => {
    const id = setTimeout(() => { try { onDone && onDone(); } catch {} }, Math.max(0, ms|0));
    return () => clearTimeout(id);
  }, [ms, onDone]);
  return null;
}

function TypingRoster() {
  // Simulated avatars + typing indicators for left stream
  const [avatars, setAvatars] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/ui/chat/avatars`);
        const dj = await r.json();
        if (alive) setAvatars(dj || {});
      } catch {}
    })();
    return () => { alive = false };
  }, []);
  const actors = [
    { name: 'Falstaff', color: '#9bdcff', key: 'falstaff' },
    { name: 'Lady Macbeth', color: '#ff5c8a', key: 'lady macbeth' },
    { name: 'Iago', color: '#ff5c8a', key: 'iago' },
    { name: 'Miranda', color: '#9bdcff', key: 'miranda' },
    { name: 'Sir Andrew', color: '#ff5c8a', key: 'sir andrew' }
  ];
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhase(p => (p + 1) % actors.length), 1800);
    return () => clearInterval(id);
  }, [actors.length]);
  return (
    <div>
      {actors.map((a, i) => (
        <div className="line typing-row" key={a.name} style={{ opacity: i <= phase ? 1 : 0.35 }}>
          <span className="avatar" style={{ backgroundImage: `url(${avatars[a.key]||''})` }} />
          <span style={{ color: a.color, fontWeight: 600, marginRight: 6 }}>{a.name}</span>
          <span className="dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function avatarFor(author, avatars) {
  const k = (author||'').toLowerCase().replace(/_/g, ' ').trim();
  if (k.startsWith('bitbard')) return avatars['bitbard'] || avatars['BitBard'] || '';
  if (k.startsWith('lady macbeth')) return avatars['lady macbeth'] || avatars['lady_macbeth'] || '';
  if (k.startsWith('sir andrew')) {
    return avatars['sir andrew'] || avatars['sir andrew aguecheek'] || avatars['sir_andrew'] || '';
  }
  if (k.startsWith('falstaff')) return avatars['falstaff'] || '';
  if (k.startsWith('miranda')) return avatars['miranda'] || '';
  if (k.startsWith('iago')) return avatars['iago'] || '';
  return '';
}


